import { FileDoneOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Empty,
  Flex,
  Input,
  List,
  Radio,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { ExamsListItem } from "../../components/exams/ExamsListItem";
import { useNavigate } from "react-router-dom";
const { Search } = Input;
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useGetCategories, useGetCatalogExams, useGetUsers } from "@hooks";
import { Category, Exam, Query, User } from "@types";
import { useState } from "react";
import {FixedSizeList} from 'react-window';
import { useExamStore } from "@store";
import { getKeycloakRealm } from "@/components/helpers";

export const ExamsPage = () => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const [examQuery, setExamQuery] = useState<Query>({sort: "dateCreated", direction: "desc"});
  const {data: exams, isLoading} = useGetCatalogExams(examQuery);
  const {data: categories, isLoading: categoriesLoading} = useGetCategories();
  const {data: users, isLoading: usersLoading} = useGetUsers();
  const currentUser = useExamStore(state => state.currentUser);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Row gutter={[32, 32]}>
        <Col xl={5} lg={5} md={24} sm={24} xs={24}>
          <Space direction="vertical" style={{ display: "flex" }}>
            <Search
              placeholder={t("pages.examsManagment.searchFilter")}
              onSearch={(value) => setExamQuery({ ...examQuery, title: value })}
              style={{ width: "100%" }}
            />
            <Typography.Title level={4} style={{ marginBottom: 0 }}>
              {t("pages.examsManagment.categoryFilter")}
            </Typography.Title>
            <Select
              mode="multiple"
              allowClear
              loading={categoriesLoading}
              style={{ width: "100%" }}
              onChange={(value) => {
                setExamQuery({ ...examQuery, category: value });
              }}
              filterOption={(input, option: any) =>
                option?.label!.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              placeholder={t("pages.examsManagment.categorySelect")}
              options={categories?.map((category: Category) => ({
                value: category.id,
                label: category.name,
              }))}
            />
            <Typography.Title level={4} style={{ marginBottom: 0 }}>
              {t("pages.examsManagment.authorFilter")}
            </Typography.Title>
            <Select
              allowClear
              style={{ width: "100%" }}
              loading={usersLoading}
              placeholder={t("pages.examsManagment.authorSelect")}
              onChange={(value) => {
                setExamQuery({ ...examQuery, owner: value });
              }}
              options={users?.items.map((user: User) => ({
                value: user.firstName + " " + user.lastName,
                label: user.firstName + " " + user.lastName,
              }))}
            />
            <Typography.Title level={4} style={{ marginBottom: 0 }}>
              {t("pages.examsManagment.statusFilter")}
            </Typography.Title>
            <Radio.Group
              defaultValue={0}
              onChange={(e) => {
                if (e.target.value === 0) {
                  setExamQuery({ ...examQuery, state: undefined });
                } else {
                  setExamQuery({ ...examQuery, state: e.target.value });
                }
              }}
            >
              <Space direction="vertical">
                <Radio value={0}>
                  {t("pages.examsManagment.statusRadioAll")}
                </Radio>
                <Radio value={1}>
                  {t("pages.examsManagment.statusRadioActive")}
                </Radio>
                <Radio value={2}>
                  {t("pages.examsManagment.statusRadioInactive")}
                </Radio>
              </Space>
            </Radio.Group>
            <Checkbox
              style={{ marginTop: 12 }}
              onChange={(e) => {
                setExamQuery({ ...examQuery, includeChallengeExams: e.target.checked || undefined });
              }}
            >
              {t("pages.examsManagment.showChallengeExams")}
            </Checkbox>
          </Space>
        </Col>
        <Col xl={19} lg={19} md={24} sm={24} xs={24}>
          <Flex justify="space-between">
            <Button
              id="new-exam-button"
              type="primary"
              icon={<FileDoneOutlined />}
              onClick={() => navigate(`/${getKeycloakRealm}/new-exam`)}
            >
              {t("pages.examsManagment.newExamButton")}
            </Button>
            <Select
              style={{ width: 200 }}
              variant="filled"
              defaultValue={0}
              onChange={(value) => {
                if (value === 0) {
                  setExamQuery({
                    ...examQuery,
                    sort: "dateCreated",
                    direction: "desc",
                  });
                } else {
                  setExamQuery({
                    ...examQuery,
                    sort: "dateCreated",
                    direction: "asc",
                  });
                }
              }}
              options={[
                { value: 0, label: t("pages.examsManagment.sortFilterNew") },
                { value: 1, label: t("pages.examsManagment.sortFilterOld") },
                // { value: 2, label: t("pages.examsManagment.sortFilterDate") },
              ]}
            />
          </Flex>
          <List style={{ paddingTop: 12 }} loading={isLoading} split={false}>
            {exams?.items.length > 0 ? (
              <motion.div
                key={"list"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <FixedSizeList
                  itemCount={exams?.items.length || 0}
                  width={"100%"}
                  itemSize={168}
                  height={window.innerHeight - 80}
                >
                  {({ index, style }) => {
                    const exam = exams?.items[index] as Exam;
                    return (
                      <div style={style} key={index}>
                        <ExamsListItem user={currentUser!} exam={exam} />
                      </div>
                    );
                  }}
                </FixedSizeList>
              </motion.div>
            ) : (
              <motion.div
                key={"empty"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Empty />
              </motion.div>
            )}
          </List>
        </Col>
      </Row>
    </motion.div>
  );
};
